import React from 'react';
import { Helmet } from 'react-helmet';
import { motion } from 'framer-motion';
import Slider from 'react-slick';
import { FaCampground, FaHotel } from 'react-icons/fa';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import useScrollToTop from '../ScrollToTop';

import "slick-carousel/slick/slick.css";
import "slick-carousel/slick/slick-theme.css";
import './place.css';

const LakeNakuru = () => {
  useScrollToTop();
  const { t } = useTranslation('nakuru');

  const topActivities = t('topActivities.list', { returnObjects: true });
  const travelTips = t('travelTips.tips', { returnObjects: true });
  const accommodationOptions = t('accommodation.options', { returnObjects: true });
  const carouselAlts = t('images.carousel', { returnObjects: true });
  const galleryAlts = t('images.gallery', { returnObjects: true });

  // ✅ All image paths now point to /public/images/
  const carouselImages = [
    '/images/_12A2062.jpg',
    '/images/rhino.jpg',
    '/images/BUFFALO.avif',
    '/images/ZEBRABIRD.jpg'
  ];

  const galleryImages = [
    '/images/nakszebra.jpg',
    '/images/NAKURU BIRD WATCH.JPG',
    '/images/rhinos.jpg'
  ];

  return (
    <motion.div
      className="place-page"
      initial={{ opacity: 0, y: 50 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.7, ease: 'easeOut' }}
    >
      {/* Helmet SEO */}
      <Helmet>
  <title>Lake Nakuru Safari | Flamingos, Rhinos & Birdwatching Paradise | Olenini Safaris</title>
  <meta
    name="description"
    content="Experience Lake Nakuru National Park with Olenini Safaris — home to flamingos, rhinos, lions, and Kenya's best birdwatching in the Great Rift Valley."
  />
  <link rel="canonical" href="https://oleninisafaris.com/lake-nakuru" />
  <meta
    name="robots"
    content="index, follow, max-snippet:-1, max-image-preview:large, max-video-preview:-1"
  />

  {/* Open Graph / Facebook */}
  <meta property="og:title" content="Lake Nakuru Safari | Olenini Safaris" />
  <meta
    property="og:description"
    content="Explore Lake Nakuru National Park — a sanctuary for flamingos, rhinos, and stunning birdlife in Kenya's Great Rift Valley."
  />
  <meta property="og:image" content="https://oleninisafaris.com/images/flamingo.jpg" />
  <meta property="og:url" content="https://oleninisafaris.com/lake-nakuru" />
  <meta property="og:type" content="place" />

  {/* Twitter */}
  <meta name="twitter:card" content="summary_large_image" />
  <meta name="twitter:title" content="Lake Nakuru Safari | Olenini Safaris" />
  <meta
    name="twitter:description"
    content="Discover Lake Nakuru’s flamingos, rhinos, and scenic landscapes with Olenini Safaris — a birdwatcher’s paradise in Kenya."
  />
  <meta name="twitter:image" content="https://oleninisafaris.com/images/flamingo.jpg" />

  {/* JSON-LD Structured Data */}
  <script type="application/ld+json">
    {`
    {
      "@context": "https://schema.org",
      "@type": "NationalPark",
      "name": "Lake Nakuru National Park",
      "description": "Lake Nakuru National Park, located in Kenya's Great Rift Valley, is known for its large flamingo populations, rhinos, and diverse wildlife.",
      "image": "https://oleninisafaris.com/images/flamingo.jpg",
      "url": "https://oleninisafaris.com/lake-nakuru",
      "geo": {
        "@type": "GeoCoordinates",
        "latitude": -0.3667,
        "longitude": 36.0833
      },
      "containedInPlace": {
        "@type": "TouristDestination",
        "name": "Kenya"
      },
      "provider": {
        "@type": "TravelAgency",
        "name": "Olenini Tours & Safaris",
        "url": "https://oleninisafaris.com"
      },
      "sameAs": [
        "https://en.wikipedia.org/wiki/Lake_Nakuru_National_Park",
        "https://www.google.com/maps/place/Lake+Nakuru+National+Park"
      ]
    }
    `}
  </script>
</Helmet>


      {/* Hero Section */}
      <section className="place-hero">
        <img
          src="/images/flamingo.jpg"
          alt={t('heroText.title')}
          className="hero-image"
        />
        <div className="hero-text">
          <h1>{t('heroText.title')}</h1>
          <p>{t('heroText.description')}</p>
        </div>
      </section>

      {/* About Section */}
      <section className="place-about">
        <h2>{t('placeAbout.title')}</h2>
        <p dangerouslySetInnerHTML={{ __html: t('placeAbout.description') }} />
      </section>

      {/* Highlights Section */}
      <section className="place-highlights">
        <div className="card">
          <h3>{t('topActivities.title')}</h3>
          <ul>
            {topActivities.map((activity, i) => (
              <li key={i} dangerouslySetInnerHTML={{ __html: activity }} />
            ))}
          </ul>
        </div>

        <div className="card">
          <h3>{t('wildlifeHighlights.title')}</h3>
          <p>{t('wildlifeHighlights.paragraph1')}</p>
          <p>{t('wildlifeHighlights.paragraph2')}</p>
          <p>{t('wildlifeHighlights.paragraph3')}</p>
        </div>
      </section>

      {/* Image Carousel Section */}
      <section className="place-highlights alt-layout">
        <div className="card unified-card">
          <div className="image-carousel">
            <Slider
              autoplay
              autoplaySpeed={3000}
              infinite
              slidesToShow={1}
              slidesToScroll={1}
              arrows={false}
              dots={false}
              pauseOnHover
              swipeToSlide
            >
              {carouselImages.map((src, i) => (
                <div key={i}>
                  <img
                    src={src}
                    alt={carouselAlts[i]}
                    className="slick-image"
                    loading="lazy"
                  />
                </div>
              ))}
            </Slider>
          </div>

          <div className="text-content">
            <h3>{t('travelTips.title')}</h3>
            <ul>
              {travelTips.map((tip, i) => (
                <li key={i} dangerouslySetInnerHTML={{ __html: tip }} />
              ))}
            </ul>
          </div>
        </div>
      </section>

      {/* Gallery Section */}
      <section className="place-gallery">
        <h2>{t('gallery.title')}</h2>
        <div className="gallery-grid">
          {galleryImages.map((src, i) => (
            <img
              key={i}
              src={src}
              alt={galleryAlts[i]}
              loading="lazy"
            />
          ))}
        </div>
      </section>

      {/* Accommodation Section */}
      <section className="place-accommodation">
        <h2>{t('accommodation.title')}</h2>
        <p>{t('accommodation.intro')}</p>

        <div className="accommodation-options">
          {accommodationOptions.map(({ title, description }, i) => (
            <div className="option-card" key={i}>
              {i === 0 && <FaCampground className="icon" />}
              {i === 1 && <FaHotel className="icon" />}
              <h3>{title}</h3>
              <p>{description}</p>
            </div>
          ))}
        </div>

        <p className="note">{t('accommodation.note')}</p>
      </section>

      {/* CTA Section */}
      <section className="place-cta" aria-label="Call to Action Section">
        <h2>{t('cta.title')}</h2>
        <p>{t('cta.description')}</p>
        <Link to="/contact?source=nakurudestination" className="book-now">
          {t('cta.button')}
        </Link>
      </section>
    </motion.div>
  );
};

export default LakeNakuru;
